import React, { useCallback, useState, RefObject } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';

import {
    Button,
    FileDropZone,
    FileInput,
    GradientProgressBar,
    IconFile,
    IconTrqkLogoRounded,
    IconTrqkMark,
    IconUploadCloud,
    IconX,
    SoundWavesBackgroundZoomed,
    Warning,
} from '@trqk/components';
import { queryStrings } from '@trqk/resolvers';
import { constants, getFileSize, getWebpackEnvVar } from '@trqk/utils';
import { useInterval } from '@trqk/hooks';

import { PATHS } from '../../../constants';
import { UploadErrorMessage } from './components/UploadErrorMessage';
import { ErrorMessage } from './components/ErrorMessage';
import { useModal, useTrqkStatementView, useToast } from '../../../hooks';
import { RequestInvitation } from '../../modals';
import { getFeatureFlags } from '../../../featureFlags';

import './tryout.css';

const { API_ERROR_MESSAGES, MAX_STATEMENT_SIZE_MB, WARNING_STATEMENT_SIZE_MB } =
    constants;
const { INGEST_PRO_STATEMENT_V2, UPLOAD_TO_S3 } = queryStrings;

const DropZoneContent = (): JSX.Element => (
    <>
        <div className="flex-initial">
            <IconUploadCloud fillColor="white" width={160} />
        </div>
        <div className="flex-initial text-center">
            <h4 className="text-white">
                Drop CSV anywhere
                <br />
                for processing
            </h4>
        </div>
    </>
);

const getProgressRate = (totalFileSize: number): number => {
    // totalFileSize is the size in bytes for the uploaded file(s).
    const sizeInKb = getFileSize({
        bytes: totalFileSize,
        unit: 'KB',
    });

    if (sizeInKb < 20) {
        return 0.4;
    }
    if (sizeInKb < 500) {
        return 0.3;
    }
    if (sizeInKb < 1000) {
        return 0.2;
    }
    if (sizeInKb < 10000) {
        return 0.1;
    }
    if (sizeInKb < 50000) {
        return 0.05;
    }

    return 0.025;
};

interface PROUploadErrorMessage {
    test: boolean;
    Component(): JSX.Element;
}

/**
 * Shows a page that allows the user to upload a CSV of their PRO statement in order to see some
 * initial data visualizations. The goal is to convince the user to register.
 *
 * We may eventually support multi-file uploads, but for now it's just set up for
 * single-file uploads.
 *
 * The data flow goes like this:
 * 1. User selects a CSV file containing a PRO statement to upload, either with the FileInput or
 *  the FileDropZone
 * 2. The CSV is uploaded to the S3 bucket
 * 3. Once uploaded, a URL for the object in S3 is used to send a POST request to the PRO Ingestion
 *  service
 * 4. Once the PRO ingestion completes, the user is sent to the Insights page to see data
 *  visualizations for their PRO statement
 */
export const Tryout = (): JSX.Element => {
    const { limitStatementUploadFileSize } = getFeatureFlags();
    const bucketName = getWebpackEnvVar('web', 'AWS_S3_PRO_STATEMENT_BUCKET');
    const bucketRegion = getWebpackEnvVar('web', 'AWS_REGION');
    const identityPoolId = getWebpackEnvVar('web', 'AWS_IDENTITY_POOL_ID');
    const proIngestionV2Url = getWebpackEnvVar('web', 'PRO_INGESTION_V2_URL');
    const proStatementViewUrl = getWebpackEnvVar(
        'web',
        'PRO_STATEMENT_VIEW_URL',
    );
    // File input tags are a bit tricky in React. We use local state to get access to the current
    // <input> ref so that we can reset it after the user selects a file/files.
    // See: https://reactjs.org/docs/uncontrolled-components.html#the-file-input-tag
    const [fileInputRef, setFileInputRef] =
        useState<RefObject<HTMLInputElement> | null>(null);
    const [hasUploadError, setHasUploadError] = useState(false);
    const [hasIngestionError, setHasIngestionError] = useState(false);
    const [hasTransformationTooLargeError, setHasTransformationTooLargeError] =
        useState(false);
    const [hasFileSizeError, setHasFileSizeError] = useState(false);
    const [progress, setProgress] = useState(0);
    const [uploadWasCanceled, setUploadWasCanceled] = useState(false);
    const [totalFileSize, setTotalFileSize] = useState(0);
    const [selectedWrongFileType, setSelectedWrongFileType] = useState(false);
    const [droppedMultipleFiles, setDroppedMultipleFiles] = useState(false);
    const [droppedWrongFileType, setDroppedWrongFileType] = useState(false);
    const [filename, setFilename] = useState('');
    const history = useHistory();
    const modal = useModal();
    const toast = useToast();

    const onDashboardLoaded = useCallback((): void => {
        if (uploadWasCanceled) {
            setUploadWasCanceled(false);
        }
        if (hasIngestionError) {
            setHasIngestionError(false);
        }

        history.push(PATHS.STATEMENTS_PREVIEW);
    }, []);

    const [useTrqkStatementViewInput, setUseTrqkStatementViewInput] = useState({
        filename: '',
        onDashboardLoaded,
        statementId: null as Maybe<string>,
        proStatementViewUrl,
    });

    // Step 3: poll for completed statementView with useTrqkStatementView
    const useTrqkStatementViewResult = useTrqkStatementView(
        useTrqkStatementViewInput,
    );

    if (useTrqkStatementViewResult.error) {
        if (
            useTrqkStatementViewResult?.error?.message ===
            API_ERROR_MESSAGES.TRANSFORMATION_TOO_LARGE
        ) {
            if (!hasTransformationTooLargeError) {
                setHasTransformationTooLargeError(true);
            }
        } else if (!hasIngestionError) {
            setHasIngestionError(true);
        }
    }

    // Step 2: ingest statement and set value of useTrqkStatementViewInput
    const [ingestProStatementV2, ingestProStatementV2Query] = useMutation(
        INGEST_PRO_STATEMENT_V2,
        {
            onCompleted: data => {
                const error = data?.ingestProStatementV2?.error;

                if (error) {
                    setHasIngestionError(true);
                } else {
                    setHasIngestionError(false);

                    const ingestedFilename =
                        data?.ingestProStatementV2?.filename ?? '';
                    const statementId =
                        data?.ingestProStatementV2?.dashboard?.statement_info
                            ?.statement_id ?? null;

                    setUseTrqkStatementViewInput({
                        filename: ingestedFilename,
                        onDashboardLoaded,
                        statementId,
                        proStatementViewUrl,
                    });
                }

                if (uploadWasCanceled) {
                    setUploadWasCanceled(false);
                }
            },
        },
    );

    // Step 1: upload the statement to S3
    const [uploadToS3, uploadToS3Query] = useMutation(UPLOAD_TO_S3, {
        onCompleted: data => {
            const error = data?.uploadToS3?.error;

            if (error) {
                setHasUploadError(true);
                setFilename('');
            } else {
                setHasUploadError(false);
            }

            if (uploadWasCanceled) {
                setUploadWasCanceled(false);
                setFilename('');
            } else {
                const result = data?.uploadToS3?.result;

                // We were able to upload files successfully, so we trigger the API call to the
                // PRO Ingestion lambda function.
                if (result) {
                    ingestProStatementV2({
                        variables: {
                            filename,
                            proIngestionV2Url,
                            statementUrl: result.location,
                        },
                    });
                }
            }

            if (fileInputRef !== null && fileInputRef.current !== null) {
                // If the FileInput was used, whether or not the upload succeeds, we reset the
                // <input> ref's current value to an empty string.
                fileInputRef.current.value = '';
            }
        },
    });

    const isLoading =
        uploadToS3Query.loading ||
        ingestProStatementV2Query.loading ||
        useTrqkStatementViewResult.loading;

    const isInProgress = !uploadWasCanceled && isLoading;

    useInterval(
        (): void => {
            if (progress < 100) {
                setProgress(progress + getProgressRate(totalFileSize));
            }
        },
        isInProgress ? 50 : null,
    );

    const uploadAndIngestFile = (file: File, preUpload?: () => void): void => {
        setDroppedMultipleFiles(false);
        setSelectedWrongFileType(false);
        setHasTransformationTooLargeError(false);
        setHasIngestionError(false);

        if (
            limitStatementUploadFileSize &&
            getFileSize({ bytes: file.size, unit: 'MB' }) >
                MAX_STATEMENT_SIZE_MB
        ) {
            setHasFileSizeError(true);
        } else {
            if (
                getFileSize({ bytes: file.size, unit: 'MB' }) >
                WARNING_STATEMENT_SIZE_MB
            ) {
                toast.add({
                    content:
                        'Your statement is quite large. This may take a while.',
                    kind: 'info-banner',
                });
            }

            setTotalFileSize(file.size);
            setUploadWasCanceled(false);
            setProgress(0);
            setFilename(file.name);

            if (preUpload) {
                preUpload();
            }

            uploadToS3({
                variables: {
                    bucketName,
                    bucketRegion,
                    identityPoolId,
                    file,
                },
            });
        }
    };

    const handleUploadViaLink = (
        fileInput: RefObject<HTMLInputElement>,
    ): void => {
        if (fileInput.current) {
            const file = fileInput?.current?.files?.[0];

            if (file) {
                const preUpload = (): void => {
                    setFileInputRef(fileInput);
                };

                uploadAndIngestFile(file, preUpload);
            }
        }
    };

    const handleSelectedFilesWithWrongType = (): void => {
        setSelectedWrongFileType(true);
    };

    const hasError =
        droppedWrongFileType ||
        selectedWrongFileType ||
        droppedMultipleFiles ||
        hasUploadError ||
        hasIngestionError ||
        hasFileSizeError ||
        hasTransformationTooLargeError;

    const FileTypeError = (): JSX.Element => (
        <ErrorMessage
            handleUploadViaLink={handleUploadViaLink}
            handleSelectedFilesWithWrongType={handleSelectedFilesWithWrongType}
        >
            <Warning>FILE TYPE ISSUE</Warning>
            <div className="text-white text-3xl pt-3 pb-6">
                Please try again using a <span className="font-bold">.csv</span>{' '}
                file
            </div>
        </ErrorMessage>
    );

    const FileSizeError = (): JSX.Element => (
        <ErrorMessage
            handleUploadViaLink={handleUploadViaLink}
            handleSelectedFilesWithWrongType={handleSelectedFilesWithWrongType}
        >
            <Warning>FILE SIZE ISSUE</Warning>
            <div className="text-white text-2xl py-3">
                {`Statement uploads are currently limited to ${MAX_STATEMENT_SIZE_MB}MB.`}
            </div>
        </ErrorMessage>
    );

    const DroppedMultipleFilesError = (): JSX.Element => (
        <ErrorMessage
            handleUploadViaLink={handleUploadViaLink}
            handleSelectedFilesWithWrongType={handleSelectedFilesWithWrongType}
        >
            <div className="text-white text-2xl py-3">
                You can upload one statement at a time in our free tier.
            </div>
            <div className="text-white text-xl pb-3">
                If you would like to upload multiple statements,
                <br />
                please request a 14-day trial of our full app.
            </div>
            <Button
                addClasses="mb-6 respond-to-mouse"
                autoWidth={true}
                color="secondary-dark-bg"
                handleClick={(): void => {
                    modal.add({
                        Content: RequestInvitation,
                    });
                }}
            >
                <div className="mx-6">Request a Trial</div>
            </Button>
        </ErrorMessage>
    );

    const IngestionError = (): JSX.Element => (
        <ErrorMessage
            handleUploadViaLink={handleUploadViaLink}
            handleSelectedFilesWithWrongType={handleSelectedFilesWithWrongType}
        >
            <Warning>FILE INGESTION ERROR</Warning>
            <div className="text-white text-3xl pt-3 pb-6">
                Please try again using a
                <br />
                <span className="font-bold">.csv</span> statement from your PRO.
            </div>
        </ErrorMessage>
    );

    // Shown if a specific API error occurs in getTrqkStatementView - this should be no longer
    // necessary after the related issue is resolved in the backend.
    const TransformationTooLargeError = (): JSX.Element => (
        <ErrorMessage
            handleUploadViaLink={handleUploadViaLink}
            handleSelectedFilesWithWrongType={handleSelectedFilesWithWrongType}
        >
            <Warning>FILE SIZE ERROR</Warning>
            <div className="text-white text-3xl pt-3 pb-6">
                Our apologies; your statement was too large for our current
                abilities.
                <br />
                Please try again with a smaller statement.
            </div>
        </ErrorMessage>
    );

    // droppedWrongFileType is not reset until another file is dropped.
    const proUploadErrorMessages = [
        {
            test: hasFileSizeError,
            Component: FileSizeError,
        },
        {
            test: droppedWrongFileType || selectedWrongFileType,
            Component: FileTypeError,
        },
        {
            test: droppedMultipleFiles,
            Component: DroppedMultipleFilesError,
        },
        {
            test:
                !droppedWrongFileType &&
                !selectedWrongFileType &&
                hasUploadError,
            Component: UploadErrorMessage,
        },
        {
            test:
                !droppedWrongFileType &&
                !selectedWrongFileType &&
                hasIngestionError,
            Component: IngestionError,
        },
        {
            test:
                !droppedWrongFileType &&
                !selectedWrongFileType &&
                hasTransformationTooLargeError,
            Component: TransformationTooLargeError,
        },
    ] as PROUploadErrorMessage[];

    const FirstError =
        proUploadErrorMessages.find(
            (proUploadErrorMessage: PROUploadErrorMessage): boolean =>
                proUploadErrorMessage.test,
        )?.Component ?? null;

    return (
        <FileDropZone
            acceptFileTypes={['csv']}
            DropZoneContent={DropZoneContent}
            handleDrop={({
                validFiles,
                invalidFiles,
            }: HandleDropInput): void => {
                if (invalidFiles.length > 0) {
                    setDroppedWrongFileType(true);
                } else {
                    setDroppedWrongFileType(false);
                    // We only ingest one file at a time.
                    if (validFiles.length > 1) {
                        setDroppedMultipleFiles(true);
                    } else {
                        const file = validFiles[0];

                        uploadAndIngestFile(file);
                    }
                }
            }}
            overlayClasses="border-highlight-teal border-12"
        >
            <div className="w-screen h-screen upload-pro-statement text-white">
                <SoundWavesBackgroundZoomed>
                    <div className="flex justify-between h-screen">
                        <div className="flex-initial flex-col flex items-center pt-8 px-12">
                            <div className="pb-4">
                                <IconTrqkMark width={50} fillColor="white" />
                            </div>
                            <div className="pl-1">
                                <IconTrqkLogoRounded
                                    fillColor="white"
                                    width={85}
                                />
                            </div>
                        </div>
                        <div className="flex-auto flex flex-col justify-between h-full">
                            <div className="flex-auto pl-12 border-l border-solid border-quarternary-100 pt-12" />
                            <div className="flex-initial flex">
                                {!hasError && (
                                    <>
                                        <div className="border-l-4 border-solid border-white rounded-full" />
                                        <div className="ml-12">
                                            <div className="text-xs font-bold">
                                                LET&rsquo;S GET STARTED!
                                            </div>
                                            <h1 className="text-white">
                                                Drag & drop your most current
                                            </h1>
                                            <h1 className="pb-3 text-white">
                                                PRO statement (csv)
                                            </h1>
                                            or{' '}
                                            <FileInput
                                                addClasses="flex-grow mb-6"
                                                acceptFileTypes={['csv']}
                                                acceptMultiple={false}
                                                handleSelectedFilesWithWrongType={
                                                    handleSelectedFilesWithWrongType
                                                }
                                                inputId="landing-page-csv-upload"
                                                // We could actually handle multiple files here if we choose,
                                                // but it's currently set up for a single file upload.
                                                // acceptMultiple={true}
                                                handleFiles={
                                                    handleUploadViaLink
                                                }
                                            >
                                                <span className="font-semibold respond-to-mouse text-white underline cursor-pointer">
                                                    browse your files
                                                </span>
                                            </FileInput>
                                        </div>
                                    </>
                                )}
                                {hasError && FirstError && <FirstError />}
                            </div>
                            <div className="flex-auto border-l border-solid border-quarternary-100">
                                <div className="ml-12 h-16">
                                    {isInProgress && filename && (
                                        <GradientProgressBar
                                            color="purple-to-aqua"
                                            Icon={IconFile}
                                            label={filename}
                                            labelLines={2}
                                            inProgressLabel="Uploading..."
                                            progress={progress}
                                            handleCancel={(): void => {
                                                setUploadWasCanceled(true);
                                            }}
                                            width={500}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="flex-none border-l border-solid border-quarternary-100 h-64" />
                        </div>
                        <div className="flex-initial p-12">
                            <a href={PATHS.CONTACT}>
                                <IconX
                                    fillColor="tertiary-500"
                                    addClasses="respond-to-mouse"
                                />
                            </a>
                        </div>
                    </div>
                </SoundWavesBackgroundZoomed>
            </div>
        </FileDropZone>
    );
};
